import { Select } from "components/Select";
import { ISelectOption, ISelectProps } from "components/Select/Select";
import { CSSProperties, ReactElement, useId, useMemo } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "store/hooks";
import { selectAccounts, selectSelectedAccountId } from "store/WalletsStore";
import { selectAccount } from "store/WalletsStore/thunks";
import styled from "styled-components";
import { IUnlockedAccountMeta } from "types/wallet";
import { FilterLabel } from "styles/sharedStyledComponents";

const FilterGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

type TSelectAdditionalProps = Omit<
    ISelectProps,
    "value" | "onChange" | "options"
>;

export enum AccountSelectorLabelMods {
    COMMON = "common",
    FULL = "full",
}

interface IAccountSelectorProps extends TSelectAdditionalProps {
    fullWidth?: boolean;
    wrapperStyle?: CSSProperties;
    labelMode?: AccountSelectorLabelMods;
    label?: string;
}

export const AccountSelector = ({
    fullWidth = false,
    wrapperStyle,
    labelMode,
    label = "Account",
    ...selectProps
}: IAccountSelectorProps): ReactElement => {
    const dispatch = useAppDispatch();
    const accounts = useSelector(selectAccounts);
    const selectedAccountId = useSelector(selectSelectedAccountId);
    const labelId = `account-selector-label-${useId().replace(/:/g, "")}`;

    const accountOptions = useMemo(
        () =>
            accounts.map((account: IUnlockedAccountMeta) => {
                const baseOption: ISelectOption = {
                    id: account.id,
                    value: account.id,
                    label: account.name,
                };

                if (labelMode === AccountSelectorLabelMods.FULL) {
                    return {
                        ...baseOption,
                        additionalLabel: account.address,
                    };
                }

                return baseOption;
            }),
        [accounts, labelMode],
    );

    const fullWidthStyle: CSSProperties = !fullWidth ? {} : { width: "100%" };

    const {
        style,
        "aria-labelledby": ariaLabelledBy,
        ...otherSelectProps
    } = selectProps;

    return (
        <FilterGroup style={{ ...fullWidthStyle, ...wrapperStyle }}>
            <FilterLabel id={labelId}>
                {label}
            </FilterLabel>
            <Select
                id="history-filter-account-select"
                value={selectedAccountId ?? undefined}
                onChange={(accountId) => dispatch(selectAccount(accountId))}
                placeholder="Select account"
                options={accountOptions}
                style={{
                    ...style,
                    ...fullWidthStyle,
                }}
                {...otherSelectProps}
                aria-labelledby={ariaLabelledBy ?? labelId}
            />
        </FilterGroup>
    );
};
